/**
 * Ad zone catalogue.
 *
 * Every surface that can carry paid inventory is declared here, once. The ad
 * server, the rate card, the sponsor portal and the frontend all read from this
 * list — adding a placement (or the future affiliate/marketplace slot) means
 * adding a row here, not restructuring code.
 */
export type AdZoneKind = 'static' | 'interstitial' | 'rotator' | 'affiliate';

export interface AdZoneDef {
  key: string;
  label: string;
  kind: AdZoneKind;
  /** Reference creative size in px. Big-screen zones are 16:9-ish. */
  size: { w: number; h: number };
  /** Behaviour when the viewport gets narrow. */
  mobile: 'keep' | 'shrink' | 'collapse';
  /** Surfaces this zone may render on. */
  surfaces: Array<'leaderboard' | 'bigscreen' | 'scorecard' | 'landing' | 'profile'>;
  /** Rate-card guidance, ZAR cents per month, flat sponsorship. */
  indicativeFlatCentsMonthly: number;
  /** Rate-card guidance, ZAR cents per thousand impressions. */
  indicativeCpmCents: number;
  description: string;
}

export const AD_ZONES: AdZoneDef[] = [
  {
    key: 'top_banner',
    label: 'Top Banner',
    kind: 'static',
    size: { w: 970, h: 90 },
    mobile: 'shrink',
    surfaces: ['leaderboard', 'scorecard', 'landing', 'profile'],
    indicativeFlatCentsMonthly: 850_000,
    indicativeCpmCents: 4_500,
    description: 'Above the leaderboard on every viewer surface. Highest guaranteed viewability.',
  },
  {
    key: 'sidebar_primary',
    label: 'Primary Sidebar',
    kind: 'static',
    size: { w: 300, h: 600 },
    mobile: 'collapse',
    surfaces: ['leaderboard', 'profile'],
    indicativeFlatCentsMonthly: 500_000,
    indicativeCpmCents: 3_200,
    description: 'Half-page unit beside the live board. Desktop and clubhouse laptops.',
  },
  {
    key: 'sidebar_secondary',
    label: 'Secondary Sidebar',
    kind: 'static',
    size: { w: 300, h: 250 },
    mobile: 'collapse',
    surfaces: ['leaderboard'],
    indicativeFlatCentsMonthly: 280_000,
    indicativeCpmCents: 2_100,
    description: 'Medium rectangle below the primary sidebar unit.',
  },
  {
    key: 'inline_leaderboard',
    label: 'In-Feed Row',
    kind: 'static',
    size: { w: 728, h: 90 },
    mobile: 'shrink',
    surfaces: ['leaderboard'],
    indicativeFlatCentsMonthly: 420_000,
    indicativeCpmCents: 3_800,
    description: 'Injected between leaderboard rows every N positions. Unmissable on mobile.',
  },
  {
    key: 'footer_banner',
    label: 'Sticky Footer',
    kind: 'static',
    size: { w: 728, h: 90 },
    mobile: 'keep',
    surfaces: ['leaderboard', 'scorecard', 'profile'],
    indicativeFlatCentsMonthly: 320_000,
    indicativeCpmCents: 2_800,
    description: 'Pinned to the bottom of the viewport. Travels with the scorer all round.',
  },
  {
    key: 'popup_interstitial',
    label: 'Interstitial Takeover',
    kind: 'interstitial',
    size: { w: 800, h: 600 },
    mobile: 'keep',
    surfaces: ['leaderboard', 'scorecard', 'profile'],
    indicativeFlatCentsMonthly: 1_450_000,
    indicativeCpmCents: 9_500,
    description: 'Full-screen unit on a timed cadence with a skip counter. Sold exclusively per event.',
  },
  {
    key: 'bigscreen_rotator',
    label: 'Clubhouse Big Screen',
    kind: 'rotator',
    size: { w: 1920, h: 1080 },
    mobile: 'collapse',
    surfaces: ['bigscreen'],
    indicativeFlatCentsMonthly: 1_200_000,
    indicativeCpmCents: 7_500,
    description: 'Full-bleed creative rotating on the clubhouse TV between leaderboard pages.',
  },
  {
    key: 'bigscreen_ticker',
    label: 'Big Screen Ticker',
    kind: 'rotator',
    size: { w: 1920, h: 120 },
    mobile: 'collapse',
    surfaces: ['bigscreen'],
    indicativeFlatCentsMonthly: 650_000,
    indicativeCpmCents: 3_600,
    description: 'Persistent sponsor strip along the base of the clubhouse display.',
  },
  {
    key: 'affiliate_slot',
    label: 'Marketplace / Affiliate Slot',
    kind: 'affiliate',
    size: { w: 300, h: 300 },
    mobile: 'shrink',
    surfaces: ['leaderboard', 'profile', 'landing'],
    indicativeFlatCentsMonthly: 0,
    indicativeCpmCents: 0,
    description:
      'Reserved plug-in point for gear, green-fee bookings and coaching offers. Served by the same ' +
      'placement and rotation pipeline, so an affiliate feed needs no structural change later.',
  },
];

export type AdSurface = AdZoneDef['surfaces'][number];

export const AD_ZONE_KEYS: string[] = AD_ZONES.map((z) => z.key);
export const adZone = (key: string): AdZoneDef | undefined => AD_ZONES.find((z) => z.key === key);
export const zonesForSurface = (surface: AdSurface): AdZoneDef[] =>
  AD_ZONES.filter((z) => z.surfaces.includes(surface));
